<template>
<div class="mb-8">
  <div class="mb-4 flex flex-wrap items-end gap-4">
    <div v-if="isAllScope" class="text-sm text-gray-500">
      Select a university to view ceremony performance.
    </div>
    <template v-else>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Season</label>
        <select v-model="seasonKeyProxy" class="border rounded px-3 py-2 min-w-[260px]" @change="emitSeasonChange">
          <option value="all">All ceremonies</option>
          <option v-for="s in surveySeasons" :key="'ceremony-season-'+s.key" :value="s.key">{{ s.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Insight mode</label>
        <select v-model="insightsModeProxy" class="border rounded px-3 py-2 min-w-[220px]" @change="emitInsightsModeChange">
          <option value="season_insights">Season insights</option>
          <option value="season_comparison">Season comparison</option>
        </select>
      </div>
      <div v-if="ceremonyInsightsMode === 'season_comparison'">
        <label class="block text-xs text-gray-600 mb-1">Compare against</label>
        <select
          v-model="compareSeasonKeyProxy"
          class="border rounded px-3 py-2 min-w-[260px]"
          :disabled="ceremonySeasonKey === 'all' || !ceremonyCompareSeasonOptions.length"
          @change="emitCompareSeasonChange"
        >
          <option value="" v-if="!ceremonyCompareSeasonOptions.length">No comparison season</option>
          <option v-for="s in ceremonyCompareSeasonOptions" :key="'ceremony-compare-'+s.key" :value="s.key">{{ s.label }}</option>
        </select>
      </div>
      <div>
        <div class="flex items-center gap-2">
          <button @click="$emit('refresh')" class="bg-black text-white px-3 py-2 rounded text-xs">Refresh</button>
          <button
            @click="$emit('export-report')"
            :disabled="exporting || isAllScope"
            :class="['px-3 py-2 rounded text-xs border', exporting || isAllScope ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white hover:bg-gray-50']"
          >
            {{ exportButtonLabel }}
          </button>
        </div>
      </div>
    </template>
  </div>

  <div v-if="exportStatusText" class="text-xs text-gray-600 mb-3" role="status">{{ exportStatusText }}</div>
  <div v-if="exportError" role="alert" class="text-xs text-red-600 mb-3">{{ exportError }}</div>
  <div v-if="!isAllScope" class="text-xs text-gray-500 mb-3">
    Ceremony analytics are scoped to the selected season ceremonies (not rolling time windows).
  </div>

  <div v-if="isAllScope" class="text-sm text-gray-500"></div>
  <div v-else-if="isCeremonyBreakdownLoading" class="text-sm text-gray-500">Loading season-scoped ceremony analytics...</div>
  <div v-else-if="ceremonyBreakdownError" class="text-sm text-red-600">{{ ceremonyBreakdownError }}</div>
  <div v-else-if="!ceremonyRowsSorted.length" class="text-sm text-gray-500">No ceremony-level data in this scope.</div>
  <div v-else>
    <div v-if="ceremonyInsightsMode !== 'season_comparison' && ceremonyScopedHeadlineSummary" class="border rounded-lg bg-white p-4 mb-6">
      <div class="text-sm font-semibold text-gray-900 mb-1">{{ ceremonyScopedHeadlineSummary.title }}</div>
      <div class="text-sm text-gray-700">{{ ceremonyScopedHeadlineSummary.summary }}</div>
      <div class="text-xs text-gray-500 mt-2">Relevant to selected season scope: {{ ceremonySeasonScopeLabel }}</div>
    </div>
    <div v-else-if="ceremonyInsightsMode === 'season_comparison' && ceremonySeasonCompareHeadline" class="border border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-white p-4 mb-6">
      <div class="text-sm font-semibold text-purple-900 mb-1">{{ ceremonySeasonCompareHeadline.title }}</div>
      <div class="text-sm text-gray-700">{{ ceremonySeasonCompareHeadline.summary }}</div>
      <div class="text-xs text-gray-500 mt-2">Relevant to season comparison: {{ ceremonySeasonScopeLabel }} vs {{ ceremonyCompareSeasonLabel }}</div>
    </div>

    <div v-if="ceremonyInsightsMode !== 'season_comparison'" class="mb-8">
      <h3 class="text-lg font-semibold mb-3">Overview metrics</h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div
          v-for="card in ceremonyPrimaryKpiCards"
          :key="`ceremony-primary-kpi-${card.id}`"
          class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border"
        >
          <div class="text-gray-500 text-sm">
            {{ card.title }}
            <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips[card.tooltip_key] || ''" role="img" aria-label="Info">ⓘ</span>
          </div>
          <div class="text-3xl font-semibold">{{ formatScopedMetricValue(card.value, card.kind) }}</div>
          <div
            v-if="card.id === 'observed_share_amplification' && ceremonyScopedStats.observed_share_amplification_reliability_label"
            class="mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
            :class="shareReliabilityClass(ceremonyScopedStats.observed_share_amplification_reliability)"
          >
            Reliability: {{ ceremonyScopedStats.observed_share_amplification_reliability_label }}
          </div>
          <div v-if="isAdmin && ceremonyBenchmarkLabel(card.benchmark_metric_key || card.id)" class="mt-2 text-xs font-medium text-gray-600">
            Benchmark (rolling 12 months): {{ ceremonyBenchmarkLabel(card.benchmark_metric_key || card.id) }}
          </div>
        </div>
      </div>
      <div v-if="showProjectedTotalImpressions" class="mt-4 border rounded-lg bg-white p-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div>
            <div class="text-gray-500 text-sm mb-1">
              Projected Total Impressions
              <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.projected_total_impressions || ''" role="img" aria-label="Info">ⓘ</span>
            </div>
            <div class="text-3xl font-semibold">{{ projectedTotalImpressionsRange }}</div>
            <div class="mt-2 text-xs font-medium text-gray-600">
              Downloaded clips are typically widely shared, with industry estimates of between 200 and 300 views per share on average
            </div>
          </div>
          <div>
            <div class="text-gray-500 text-sm mb-1">
              <!-- Reasoning -->
            </div>
            <div class="text-sm leading-6 text-gray-700">
              <!-- {{ projectedTotalImpressionsReasoningText }} -->
            </div>
          </div>
        </div>
      </div>
      <details v-if="ceremonySecondaryKpiCards.length" class="mt-4 border rounded-lg bg-white">
        <summary class="px-4 py-3 text-sm text-gray-700 cursor-pointer select-none">More metrics ({{ ceremonySecondaryKpiCards.length }})</summary>
        <div class="px-4 pb-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div
              v-for="card in ceremonySecondaryKpiCards"
              :key="`ceremony-secondary-kpi-${card.id}`"
              class="rounded-lg p-4 bg-white border"
            >
              <div class="text-gray-500 text-sm">
                {{ card.title }}
                <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips[card.tooltip_key] || ''" role="img" aria-label="Info">ⓘ</span>
              </div>
              <div class="text-2xl font-semibold">{{ formatScopedMetricValue(card.value, card.kind) }}</div>
            </div>
          </div>
        </div>
      </details>
    </div>
    <div v-else-if="ceremonySeasonCompareData" class="mb-8">
      <h3 class="text-lg font-semibold mb-3">Season comparison overview</h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div
          v-for="card in ceremonyComparisonPrimaryKpiCards"
          :key="`ceremony-comparison-primary-kpi-${card.id}`"
          class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border"
        >
          <div class="text-gray-500 text-sm">
            {{ card.title }}
            <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips[card.tooltip_key] || ''" role="img" aria-label="Info">ⓘ</span>
          </div>
          <div class="text-3xl font-semibold">{{ formatComparisonMetricValue(card.current_value, card.kind) }}</div>
          <div class="text-xs text-gray-500 mt-1">
            Baseline ({{ ceremonyCompareSeasonLabel || 'comparison season' }}): {{ formatComparisonMetricValue(card.baseline_value, card.kind) }}
          </div>
          <div
            v-if="card.delta_pct !== null"
            class="text-xs font-semibold mt-2 inline-flex items-center gap-1"
            :class="comparisonDeltaClass(card.delta_pct)"
          >
            <span>{{ comparisonDeltaArrow(card.delta_pct) }}</span>
            <span>{{ comparisonDeltaText(card.delta_pct) }}</span>
          </div>
          <div v-else class="text-xs text-gray-400 mt-2">No baseline percentage delta</div>
        </div>
      </div>
      <details v-if="ceremonyComparisonSecondaryKpiCards.length" class="mt-4 border rounded-lg bg-white">
        <summary class="px-4 py-3 text-sm text-gray-700 cursor-pointer select-none">More comparison metrics ({{ ceremonyComparisonSecondaryKpiCards.length }})</summary>
        <div class="px-4 pb-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <div
              v-for="card in ceremonyComparisonSecondaryKpiCards"
              :key="`ceremony-comparison-secondary-kpi-${card.id}`"
              class="rounded-lg p-4 bg-white border"
            >
              <div class="text-gray-500 text-sm">
                {{ card.title }}
                <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips[card.tooltip_key] || ''" role="img" aria-label="Info">ⓘ</span>
              </div>
              <div class="text-2xl font-semibold">{{ formatComparisonMetricValue(card.current_value, card.kind) }}</div>
              <div class="text-xs text-gray-500 mt-1">
                Baseline: {{ formatComparisonMetricValue(card.baseline_value, card.kind) }}
              </div>
              <div
                v-if="card.delta_pct !== null"
                class="text-xs font-semibold mt-2 inline-flex items-center gap-1"
                :class="comparisonDeltaClass(card.delta_pct)"
              >
                <span>{{ comparisonDeltaArrow(card.delta_pct) }}</span>
                <span>{{ comparisonDeltaText(card.delta_pct) }}</span>
              </div>
            </div>
          </div>
        </div>
      </details>
    </div>
    <div v-else-if="ceremonySeasonCompareLoading" class="text-sm text-gray-500 border rounded-lg bg-white p-4 mb-8">
      <div class="flex items-start gap-2">
        <span class="spinner mt-0.5" aria-hidden="true"></span>
        <div>
          <div class="text-sm text-gray-700">{{ ceremonySeasonCompareLoadingText }}</div>
          <div class="text-xs text-gray-500 mt-1">Current status: {{ ceremonySeasonCompareJobStatusLabel }}</div>
        </div>
      </div>
    </div>
    <div v-else-if="ceremonySeasonCompareError" class="text-sm text-red-600 border rounded-lg bg-white p-4 mb-8">
      {{ ceremonySeasonCompareError }}
    </div>
    <div v-else-if="ceremonySeasonKey === 'all'" class="text-sm text-gray-500 border rounded-lg bg-white p-4 mb-8">
      Select a specific season to view comparison cards.
    </div>
    <div v-else-if="!ceremonyCompareSeasonKey || !ceremonyCompareSeasonOptions.length" class="text-sm text-gray-500 border rounded-lg bg-white p-4 mb-8">
      No comparison season is available for this selection.
    </div>
    <div v-else class="text-sm text-gray-500 border rounded-lg bg-white p-4 mb-8">
      Comparison cards are unavailable for this season pair.
    </div>

    <CeremonyBreakdownSection
      v-if="ceremonyInsightsMode !== 'season_comparison'"
      heading="By ceremony"
      :show-data-scope-control="false"
      :show-scope-hint="true"
      :show-collapse-toggle="true"
      :collapsed="ceremonyBreakdownCollapsed"
      fixed-scope-hint="Ceremony totals scoped to selected season ceremonies."
      ceremony-data-scope="season"
      :ceremony-view-mode="ceremonyViewMode"
      :ceremony-sort-mode="ceremonySortMode"
      :ceremony-chart-metric="ceremonyChartMetric"
      :is-ceremony-breakdown-loading="isCeremonyBreakdownLoading"
      :ceremony-breakdown-error="ceremonyBreakdownError"
      :ceremony-rows-sorted="ceremonyRowsSorted"
      :chart-metric-label="chartMetricLabel"
      :ceremony-title-fn="ceremonyTitle"
      :is-ceremony-top-clips-loading-fn="isCeremonyTopClipsLoading"
      :clip-name-fn="clipName"
      :is-ceremony-expanded-fn="isCeremonyExpanded"
      :metric-value-fn="metricValue"
      :ceremony-metric-width-fn="ceremonyMetricWidth"
      :tooltips="tooltips"
      @update:collapsed="ceremonyBreakdownCollapsed = $event"
      @update:ceremonyViewMode="viewModeProxy = $event"
      @update:ceremonySortMode="sortModeProxy = $event"
      @update:ceremonyChartMetric="chartMetricProxy = $event"
      @toggle-ceremony-expand="$emit('toggle-ceremony-expand', $event)"
    />

    <div v-if="ceremonyInsightsMode !== 'season_comparison'" class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 class="text-lg font-semibold mb-3">Share distribution <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.share_distribution" role="img" aria-label="Info">ⓘ</span></h3>
        <div v-if="(ceremonyScopedStats.share_platforms || []).length === 0" class="text-sm text-gray-500">No share data in season scope.</div>
        <div v-else>
          <div v-for="row in ceremonyScopedStats.share_platforms" :key="'season-share-'+row.platform" class="mb-3">
            <div class="flex justify-between text-sm mb-1"><span class="capitalize">{{ row.platform }}</span><span>{{ row.percent }}%</span></div>
            <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: row.percent + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 class="text-lg font-semibold mb-3">Timezones <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.timezones" role="img" aria-label="Info">ⓘ</span></h3>
        <div class="text-sm text-gray-600 mb-2">Timezones reached: <span class="font-semibold">{{ ceremonyScopedStats.timezones_reached || 0 }}</span></div>
        <div v-if="(ceremonyScopedStats.top_timezones || []).length === 0" class="text-sm text-gray-500">No timezone data in season scope.</div>
        <div v-else>
          <div v-for="row in ceremonyScopedStats.top_timezones" :key="'season-tz-'+row.timezone" class="mb-3">
            <div class="flex justify-between text-sm mb-1"><span>{{ row.timezone }}</span><span>{{ row.count }}</span></div>
            <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div class="h-2 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#A0C5FF]" :style="{ width: geoWidth(row.count) }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 class="text-lg font-semibold mb-3">Share distribution comparison <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.share_distribution" role="img" aria-label="Info">ⓘ</span></h3>
        <div v-if="!ceremonySeasonComparePlatformRows.length" class="text-sm text-gray-500">No share-platform comparison data in this scope.</div>
        <div v-else class="space-y-3">
          <div v-for="row in ceremonySeasonComparePlatformRows" :key="'season-compare-platform-'+row.platform" class="border rounded-lg bg-white p-3">
            <div class="flex items-center justify-between gap-2 mb-2">
              <div class="text-sm font-medium capitalize">{{ row.platform }}</div>
              <div class="text-xs font-semibold" :class="comparisonDeltaClass(row.delta_pp)">
                {{ comparisonDeltaArrow(row.delta_pp) }} {{ comparisonDeltaPointsText(row.delta_pp) }}
              </div>
            </div>
            <div class="text-xs text-gray-600 mb-2">
              {{ ceremonySeasonScopeLabel }}: {{ Number(row.current_percent || 0).toFixed(2) }}% • {{ ceremonyCompareSeasonLabel }}: {{ Number(row.baseline_percent || 0).toFixed(2) }}%
            </div>
            <div class="space-y-1">
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: `${Math.max(0, Math.min(100, Number(row.current_percent || 0))).toFixed(2)}%` }"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-purple-300 to-purple-500" :style="{ width: `${Math.max(0, Math.min(100, Number(row.baseline_percent || 0))).toFixed(2)}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 class="text-lg font-semibold mb-3">Timezones comparison <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.timezones" role="img" aria-label="Info">ⓘ</span></h3>
        <div class="text-sm text-gray-600 mb-3">
          Timezones reached: <span class="font-semibold">{{ ceremonySeasonCompareCurrentTimezonesReached }}</span>
          vs <span class="font-semibold">{{ ceremonySeasonCompareBaselineTimezonesReached }}</span>
          <span class="ml-1 text-xs font-semibold" :class="comparisonDeltaClass(ceremonySeasonCompareTimezonesReachedDelta)">{{ comparisonDeltaArrow(ceremonySeasonCompareTimezonesReachedDelta) }} {{ comparisonDeltaCountText(ceremonySeasonCompareTimezonesReachedDelta) }}</span>
        </div>
        <div v-if="!ceremonySeasonCompareTimezoneRows.length" class="text-sm text-gray-500">No timezone comparison data in this scope.</div>
        <div v-else class="space-y-3">
          <div v-for="row in ceremonySeasonCompareTimezoneRows" :key="'season-compare-timezone-'+row.timezone" class="border rounded-lg bg-white p-3">
            <div class="flex items-center justify-between gap-2 mb-1">
              <div class="text-sm font-medium">{{ row.timezone }}</div>
              <div class="text-xs font-semibold" :class="comparisonDeltaClass(row.delta_count)">
                {{ comparisonDeltaArrow(row.delta_count) }} {{ comparisonDeltaCountText(row.delta_count) }}
              </div>
            </div>
            <div class="text-xs text-gray-600 mb-2">
              {{ ceremonySeasonScopeLabel }}: {{ Number(row.current_count || 0).toLocaleString() }} ({{ Number(row.current_percent || 0).toFixed(2) }}%)
              • {{ ceremonyCompareSeasonLabel }}: {{ Number(row.baseline_count || 0).toLocaleString() }} ({{ Number(row.baseline_percent || 0).toFixed(2) }}%)
            </div>
            <div class="space-y-1">
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#A0C5FF]" :style="{ width: comparisonRelativeWidth(row.current_count, ceremonySeasonCompareTimezoneMaxCount) }"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-purple-300 to-purple-500" :style="{ width: comparisonRelativeWidth(row.baseline_count, ceremonySeasonCompareTimezoneMaxCount) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8">
      <h3 class="text-lg font-semibold mb-3">{{ ceremonyInsightsMode === 'season_comparison' ? 'Top sources comparison' : 'Top sources' }} <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.top_sources" role="img" aria-label="Info">ⓘ</span></h3>
      <template v-if="ceremonyInsightsMode === 'season_comparison'">
        <div v-if="!ceremonySeasonCompareSourceRows.length" class="text-sm text-gray-500">No source comparison data in this scope.</div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div v-for="row in ceremonySeasonCompareSourceRows" :key="'season-compare-source-'+row.source" class="border rounded-lg bg-white p-3">
            <div class="flex items-center justify-between gap-2 mb-1">
              <div class="text-sm font-medium break-all">{{ row.source }}</div>
              <div class="text-xs font-semibold" :class="comparisonDeltaClass(row.delta_pp)">
                {{ comparisonDeltaArrow(row.delta_pp) }} {{ comparisonDeltaPointsText(row.delta_pp) }}
              </div>
            </div>
            <div class="text-xs text-gray-600 mb-2">
              {{ ceremonySeasonScopeLabel }}: {{ Number(row.current_percent || 0).toFixed(2) }}%
              • {{ ceremonyCompareSeasonLabel }}: {{ Number(row.baseline_percent || 0).toFixed(2) }}%
            </div>
            <div class="space-y-1">
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#34D399]" :style="{ width: `${Math.max(0, Math.min(100, Number(row.current_percent || 0))).toFixed(2)}%` }"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-purple-300 to-purple-500" :style="{ width: `${Math.max(0, Math.min(100, Number(row.baseline_percent || 0))).toFixed(2)}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="(ceremonyScopedStats.top_sources || []).length === 0" class="text-sm text-gray-500">No referrer data in season scope.</div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div v-for="row in ceremonyScopedStats.top_sources" :key="'season-source-'+row.source" class="mb-3">
              <div class="flex justify-between text-sm mb-1"><span>{{ row.source }}</span><span>{{ row.percent }}%</span></div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#D1FAE5]" :style="{ width: row.percent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <template v-if="ceremonyInsightsMode === 'season_comparison'">
      <div class="mt-8 pt-6 border-t">
        <div class="flex items-center justify-between gap-3 mb-3">
          <h3 class="text-lg font-semibold">Season comparison insights</h3>
          <button
                  @click="$emit('refresh')"
            :disabled="ceremonySeasonLoading || ceremonySeasonInsightsLoading || ceremonySeasonFactsLoading || ceremonySeasonCompareLoading"
            :class="['px-3 py-1.5 rounded border text-xs', (ceremonySeasonLoading || ceremonySeasonInsightsLoading || ceremonySeasonFactsLoading || ceremonySeasonCompareLoading) ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white hover:bg-gray-50']"
          >
            {{ (ceremonySeasonLoading || ceremonySeasonInsightsLoading || ceremonySeasonFactsLoading || ceremonySeasonCompareLoading) ? 'Loading...' : 'Refresh insights' }}
          </button>
        </div>
        <div v-if="ceremonySeasonCompareLoading" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
          <div class="flex items-start gap-2">
            <span class="spinner mt-0.5" aria-hidden="true"></span>
            <div>
              <div class="text-sm text-gray-700">{{ ceremonySeasonCompareLoadingText }}</div>
              <div class="text-xs text-gray-500 mt-1">Current status: {{ ceremonySeasonCompareJobStatusLabel }}</div>
            </div>
          </div>
        </div>
        <div v-else-if="ceremonySeasonCompareError" class="text-sm text-red-600 border rounded-lg bg-white p-4">
          {{ ceremonySeasonCompareError }}
        </div>
        <div v-else-if="ceremonySeasonKey === 'all'" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
          Select a specific season to compare against another season.
        </div>
        <div v-else-if="!ceremonyCompareSeasonKey || !ceremonyCompareSeasonOptions.length" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
          No comparison season is available for this selection.
        </div>
        <div v-else-if="!ceremonySeasonCompareData" class="text-sm text-gray-500 border rounded-lg bg-white p-4">
          Season comparison is unavailable for this season pair.
        </div>
        <div v-else class="space-y-4">
          <div v-if="ceremonySeasonCompareKeyInsights.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="card in ceremonySeasonCompareKeyInsights"
              :key="`season-compare-key-${card.insight_id || card.headline}`"
              class="border rounded-lg bg-white p-4"
            >
              <div class="flex items-center justify-between gap-2 mb-1">
                <div class="text-xs uppercase tracking-wide text-gray-500">{{ engagementThemeText(card.theme) }}</div>
                <div class="text-xs text-gray-500">Strength {{ card.strength_score }}</div>
              </div>
              <div class="text-sm font-semibold text-purple-900 mb-2">{{ card.headline }}</div>
              <div class="text-sm text-gray-700">{{ card.summary }}</div>
            </div>
          </div>

          <BarComparisonChart
            v-if="ceremonySeasonCompareMetricRows.length"
            title="Largest season-over-season metric deltas"
            :rows="ceremonySeasonCompareMetricRows"
            :label-fn="seasonCompareMetricLabel"
            :value-fn="seasonCompareMetricChartValue"
            :show-row-limit="false"
            :show-orientation="false"
            :max-height-px="340"
          >
            <template #value="{ row }">
              {{ seasonCompareMetricDeltaText(row) }}
            </template>
          </BarComparisonChart>
        </div>
      </div>
    </template>
    <template v-else>
      <EngagementInsightsCardsSection
        title="Engagement insights"
        :show-refresh-button="false"
        :loading="ceremonySeasonInsightsLoading"
        :error="ceremonySeasonInsightsError"
        :key-insights="ceremonyScopedKeyInsights"
        :insight-cards="ceremonyScopedInsightCards"
        :deterministic-collapsed="ceremonyDeterministicInsightsCollapsed"
        no-data-message="No insights available for this season scope."
        @toggle-deterministic="$emit('toggle-deterministic-insights')"
      />
    </template>
  </div>
</div>

</template>

<script>
import BarComparisonChart from '../BarComparisonChart.vue'
import CeremonyBreakdownSection from '../shared/CeremonyBreakdownSection.vue'
import EngagementInsightsCardsSection from '../shared/EngagementInsightsCardsSection.vue'

export default {
  name: 'CeremonyPerformancePanel',
  components: {
    BarComparisonChart,
    CeremonyBreakdownSection,
    EngagementInsightsCardsSection,
  },
  data() {
    return {
      ceremonyBreakdownCollapsed: false,
    }
  },
  props: {
    isAllScope: { type: Boolean, default: false },
    surveySeasons: { type: Array, default: () => [] },
    ceremonySeasonKey: { type: String, default: 'all' },
    ceremonyInsightsMode: { type: String, default: 'season_insights' },
    ceremonyCompareSeasonKey: { type: String, default: '' },
    ceremonySeasonRows: { type: Array, default: () => [] },
    ceremonySeasonLoading: { type: Boolean, default: false },
    ceremonySeasonError: { type: String, default: '' },
    ceremonySeasonScopedStats: { type: Object, default: () => ({}) },
    ceremonySeasonScopedInsights: { type: Object, default: null },
    ceremonySeasonInsightsLoading: { type: Boolean, default: false },
    ceremonySeasonInsightsError: { type: String, default: '' },
    ceremonySeasonFactsLoading: { type: Boolean, default: false },
    ceremonySeasonCompareData: { type: Object, default: null },
    ceremonySeasonCompareLoading: { type: Boolean, default: false },
    ceremonySeasonCompareError: { type: String, default: '' },
    ceremonySeasonCompareJobStatus: { type: String, default: '' },
    ceremonyDeterministicInsightsCollapsed: { type: Boolean, default: true },
    exporting: { type: Boolean, default: false },
    exportError: { type: String, default: '' },
    exportStatusText: { type: String, default: '' },
    exportButtonLabel: { type: String, default: 'Export PDF' },
    ceremonyViewMode: { type: String, default: 'table' },
    ceremonySortMode: { type: String, default: 'date_asc' },
    ceremonyChartMetric: { type: String, default: 'total_views' },
    expandedCeremonies: { type: Object, default: () => ({}) },
    ceremonyTopClipsLoading: { type: Object, default: () => ({}) },
    clipNameBySubmission: { type: Object, default: () => ({}) },
    selectedSlug: { type: String, default: '' },
    tooltips: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
  },
  computed: {
    seasonKeyProxy: {
      get() { return this.ceremonySeasonKey },
      set(value) { this.$emit('update:ceremonySeasonKey', String(value || 'all')) },
    },
    insightsModeProxy: {
      get() { return this.ceremonyInsightsMode },
      set(value) { this.$emit('update:ceremonyInsightsMode', String(value || 'season_insights')) },
    },
    compareSeasonKeyProxy: {
      get() { return this.ceremonyCompareSeasonKey },
      set(value) { this.$emit('update:ceremonyCompareSeasonKey', String(value || '')) },
    },
    viewModeProxy: {
      get() { return this.ceremonyViewMode },
      set(value) { this.$emit('update:ceremonyViewMode', String(value || 'table')) },
    },
    sortModeProxy: {
      get() { return this.ceremonySortMode },
      set(value) { this.$emit('update:ceremonySortMode', String(value || 'date_asc')) },
    },
    chartMetricProxy: {
      get() { return this.ceremonyChartMetric },
      set(value) { this.$emit('update:ceremonyChartMetric', String(value || 'total_views')) },
    },
    ceremonyScopedStats() {
      const raw = this.ceremonySeasonScopedStats && typeof this.ceremonySeasonScopedStats === 'object'
        ? this.ceremonySeasonScopedStats
        : {}
      return {
        unique_views: Number(raw.unique_views || 0),
        total_views: Number(raw.total_views || 0),
        total_shares: Number(raw.total_shares || 0),
        total_downloads: Number(raw.total_downloads || 0),
        graduates: Number(raw.graduates || 0),
        graduate_amplification_index: (raw.graduate_amplification_index ?? null),
        graduate_amplification_index_label: String(raw.graduate_amplification_index_label || ''),
        projected_total_impressions_range: String(raw.projected_total_impressions_range || ''),
        projected_total_impressions_lower_multiplier: Number(raw.projected_total_impressions_lower_multiplier || 0),
        projected_total_impressions_upper_multiplier: Number(raw.projected_total_impressions_upper_multiplier || 0),
        additional_audience_per_graduate: (raw.additional_audience_per_graduate ?? null),
        additional_audience_per_graduate_label: String(raw.additional_audience_per_graduate_label || ''),
        observed_share_amplification: (raw.observed_share_amplification ?? null),
        observed_share_amplification_label: String(raw.observed_share_amplification_label || ''),
        observed_share_amplification_reliability: String(raw.observed_share_amplification_reliability || ''),
        observed_share_amplification_reliability_label: String(raw.observed_share_amplification_reliability_label || ''),
        download_rate: (raw.download_rate ?? null),
        download_rate_label: String(raw.download_rate_label || ''),
        metric_tooltips: raw.metric_tooltips && typeof raw.metric_tooltips === 'object' ? raw.metric_tooltips : {},
        benchmark_yearly: raw.benchmark_yearly && typeof raw.benchmark_yearly === 'object' ? raw.benchmark_yearly : null,
        sessions: Number(raw.sessions || 0),
        page_views: Number(raw.page_views || 0),
        unique_clips_shared: Number(raw.unique_clips_shared || 0),
        share_platforms: Array.isArray(raw.share_platforms) ? raw.share_platforms : [],
        timezones_reached: Number(raw.timezones_reached || 0),
        top_timezones: Array.isArray(raw.top_timezones) ? raw.top_timezones : [],
        top_sources: Array.isArray(raw.top_sources) ? raw.top_sources : [],
      }
    },
    ceremonyScopedInsights() {
      const row = this.ceremonySeasonScopedInsights
      return row && typeof row === 'object' ? row : null
    },
    ceremonyScopedHeadlineSummary() {
      const row = this.ceremonyScopedInsights?.headline_summary
      return row && typeof row === 'object' ? row : null
    },
    ceremonyScopedKeyInsights() {
      const rows = this.ceremonyScopedInsights?.key_insights
      return Array.isArray(rows) ? rows : []
    },
    ceremonyScopedInsightCards() {
      const rows = this.ceremonyScopedInsights?.questions
      return Array.isArray(rows) ? rows : []
    },
    projectedTotalImpressionsRange() {
      return String(this.ceremonyScopedStats?.projected_total_impressions_range || '').trim()
    },
    showProjectedTotalImpressions() {
      return this.selectedSlug === 'wolverhampton'
        && this.projectedTotalImpressionsRange !== ''
        && this.projectedTotalImpressionsRange !== '0 - 0'
    },
    projectedTotalImpressionsReasoningText() {
      if (this.selectedSlug === 'wolverhampton') {
        return ''
      }
      return ''
    },
    ceremonySeasonScopeLabel() {
      if (this.ceremonySeasonKey === 'all') return 'All ceremonies'
      const season = (this.surveySeasons || []).find(s => s.key === this.ceremonySeasonKey)
      return String(season?.label || 'Selected season')
    },
    ceremonyCompareSeasonOptions() {
      if (this.ceremonySeasonKey === 'all') return []
      const seasons = Array.isArray(this.surveySeasons) ? this.surveySeasons : []
      return seasons.filter(s => String(s?.key || '') && s.key !== this.ceremonySeasonKey)
    },
    ceremonyCompareSeasonLabel() {
      const found = (this.ceremonyCompareSeasonOptions || []).find(s => s.key === this.ceremonyCompareSeasonKey)
      return String(found?.label || '')
    },
    ceremonySeasonCompareHeadline() {
      const row = this.ceremonySeasonCompareData?.headline_summary
      return row && typeof row === 'object' ? row : null
    },
    ceremonySeasonCompareKeyInsights() {
      const rows = this.ceremonySeasonCompareData?.key_insights
      return Array.isArray(rows) ? rows : []
    },
    ceremonySeasonCompareMetricRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.metric_deltas)
        ? this.ceremonySeasonCompareData.metric_deltas
        : []
      return [...rows]
        .sort((a, b) => Number(b?.abs_strength || 0) - Number(a?.abs_strength || 0))
        .slice(0, 10)
    },
    ceremonySeasonComparePlatformRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.platform_deltas)
        ? this.ceremonySeasonCompareData.platform_deltas
        : []
      return [...rows]
        .sort((a, b) => Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0)))
        .slice(0, 8)
    },
    ceremonySeasonCompareSourceRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.source_deltas)
        ? this.ceremonySeasonCompareData.source_deltas
        : []
      return [...rows]
        .sort((a, b) => Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0)))
        .slice(0, 8)
    },
    ceremonySeasonCompareTimezoneRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.timezone_deltas)
        ? this.ceremonySeasonCompareData.timezone_deltas
        : []
      return [...rows]
        .sort((a, b) => {
          const bCount = Math.abs(Number(b?.delta_count || 0))
          const aCount = Math.abs(Number(a?.delta_count || 0))
          if (bCount !== aCount) return bCount - aCount
          return Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0))
        })
        .slice(0, 8)
    },
    ceremonySeasonCompareTimezoneMaxCount() {
      const rows = this.ceremonySeasonCompareTimezoneRows
      const maxCount = rows.reduce((max, row) => {
        const cur = Number(row?.current_count || 0)
        const base = Number(row?.baseline_count || 0)
        return Math.max(max, cur, base)
      }, 0)
      return maxCount > 0 ? maxCount : 1
    },
    ceremonySeasonCompareCurrentTimezonesReached() {
      return Number(this.ceremonySeasonCompareData?.current_scope?.summary_snapshot?.timezones_reached || 0)
    },
    ceremonySeasonCompareBaselineTimezonesReached() {
      return Number(this.ceremonySeasonCompareData?.baseline_scope?.summary_snapshot?.timezones_reached || 0)
    },
    ceremonySeasonCompareTimezonesReachedDelta() {
      return this.ceremonySeasonCompareCurrentTimezonesReached - this.ceremonySeasonCompareBaselineTimezonesReached
    },
    ceremonyScopedKpiCards() {
      return [
        { id: 'total_views', title: 'Total clip views', value: this.ceremonyScopedStats.total_views, kind: 'count', tooltip_key: 'total_views' },
        { id: 'unique_views', title: 'Unique clip views', value: this.ceremonyScopedStats.unique_views, kind: 'count', tooltip_key: 'unique_views' },
        {
          id: 'graduate_amplification_index',
          title: 'Graduate Amplification Index',
          value: this.ceremonyScopedStats.graduate_amplification_index,
          kind: 'amplification_index',
          tooltip_key: 'graduate_amplification_index',
          benchmark_metric_key: 'graduate_amplification_index',
        },
        {
          id: 'observed_share_amplification',
          title: 'Views Generated per Share',
          value: this.ceremonyScopedStats.observed_share_amplification,
          kind: 'rate',
          tooltip_key: 'observed_share_amplification',
          benchmark_metric_key: 'observed_share_amplification',
        },
        {
          id: 'additional_audience_per_graduate',
          title: 'Additional Audience per Graduate',
          value: this.ceremonyScopedStats.additional_audience_per_graduate,
          kind: 'rate',
          tooltip_key: 'additional_audience_per_graduate',
        },
        { id: 'total_shares', title: 'Total clip shares', value: this.ceremonyScopedStats.total_shares, kind: 'count', tooltip_key: 'total_shares' },
        { id: 'total_downloads', title: 'Total downloads', value: this.ceremonyScopedStats.total_downloads, kind: 'count', tooltip_key: 'total_downloads' },
        { id: 'unique_clips_shared', title: 'Unique clips shared', value: this.ceremonyScopedStats.unique_clips_shared, kind: 'count', tooltip_key: 'unique_clips_shared' },
      ]
    },
    ceremonyPrimaryKpiCards() {
      const primaryIds = new Set([
        'total_views',
        'unique_views',
        'graduate_amplification_index',
        'observed_share_amplification',
      ])
      return this.ceremonyScopedKpiCards.filter(card => primaryIds.has(card.id))
    },
    ceremonySecondaryKpiCards() {
      const primaryIds = new Set(this.ceremonyPrimaryKpiCards.map(card => card.id))
      return this.ceremonyScopedKpiCards.filter(card => !primaryIds.has(card.id))
    },
    ceremonyComparisonKpiCards() {
      const current = this.ceremonySeasonCompareData?.current_scope?.summary_snapshot || {}
      const baseline = this.ceremonySeasonCompareData?.baseline_scope?.summary_snapshot || {}
      const metricDefs = [
        { id: 'unique_views', title: 'Unique clip views', key: 'total_unique_views', kind: 'count', tooltip_key: 'unique_views' },
        { id: 'total_views', title: 'Total clip views', key: 'total_views', kind: 'count', tooltip_key: 'total_views' },
        { id: 'graduate_amplification_index', title: 'Graduate Amplification Index', key: 'graduate_amplification_index', kind: 'amplification_index', tooltip_key: 'graduate_amplification_index' },
        { id: 'additional_audience_per_graduate', title: 'Additional Audience per Graduate', key: 'additional_audience_per_graduate', kind: 'rate', tooltip_key: 'additional_audience_per_graduate' },
        { id: 'observed_share_amplification', title: 'Views Generated per Share', key: 'observed_share_amplification', kind: 'rate', tooltip_key: 'observed_share_amplification' },
        { id: 'total_downloads', title: 'Total downloads', key: 'total_downloads', kind: 'count', tooltip_key: 'total_downloads' },
        { id: 'unique_clips_shared', title: 'Unique clips shared', key: 'unique_clips_shared', kind: 'count', tooltip_key: 'unique_clips_shared' },
        { id: 'total_shares', title: 'Total clip shares', key: 'total_shares', kind: 'count', tooltip_key: 'total_shares' },
        { id: 'shares_per_100_views', title: 'Shares per 100 views', key: 'shares_per_100_views', kind: 'rate', tooltip_key: 'share_distribution' },
        { id: 'downloads_per_100_views', title: 'Downloads per 100 views', key: 'downloads_per_100_views', kind: 'rate', tooltip_key: 'total_downloads' },
      ]
      return metricDefs.map((def) => {
        const currentValue = Number(current?.[def.key] || 0)
        const baselineValue = Number(baseline?.[def.key] || 0)
        const deltaPct = baselineValue > 0 ? ((currentValue - baselineValue) / baselineValue) * 100 : null
        return {
          ...def,
          current_value: currentValue,
          baseline_value: baselineValue,
          delta_pct: Number.isFinite(deltaPct) ? deltaPct : null,
        }
      })
    },
    ceremonyComparisonPrimaryKpiCards() {
      const primaryIds = new Set([
        'total_views',
        'unique_views',
        'graduate_amplification_index',
        'observed_share_amplification',
      ])
      return this.ceremonyComparisonKpiCards.filter(card => primaryIds.has(card.id))
    },
    ceremonyComparisonSecondaryKpiCards() {
      const primaryIds = new Set(this.ceremonyComparisonPrimaryKpiCards.map(card => card.id))
      return this.ceremonyComparisonKpiCards.filter(card => !primaryIds.has(card.id))
    },
    isCeremonyBreakdownLoading() {
      return this.ceremonySeasonLoading
    },
    ceremonyBreakdownError() {
      return this.ceremonySeasonError
    },
    ceremonyRowsBase() {
      const rows = this.ceremonySeasonRows
      return Array.isArray(rows)
        ? rows.filter(row => !this.isUnmappedCeremonyRow(row))
        : []
    },
    ceremonyRowsSorted() {
      const rows = [...this.ceremonyRowsBase]
      const dt = (row) => {
        if (!row || !row.ceremony_datetime) return 0
        const t = new Date(row.ceremony_datetime).getTime()
        return Number.isFinite(t) ? t : 0
      }
      if (this.ceremonySortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
      }
      if (this.ceremonySortMode === 'views_desc') {
        return rows.sort((a, b) => (b.total_views || 0) - (a.total_views || 0) || dt(a) - dt(b))
      }
      if (this.ceremonySortMode === 'shares_desc') {
        return rows.sort((a, b) => (b.total_shares || 0) - (a.total_shares || 0) || dt(a) - dt(b))
      }
      if (this.ceremonySortMode === 'downloads_desc') {
        return rows.sort((a, b) => (b.total_downloads || 0) - (a.total_downloads || 0) || dt(a) - dt(b))
      }
      return rows.sort((a, b) => dt(a) - dt(b) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
    },
    chartMetricLabel() {
      if (this.ceremonyChartMetric === 'unique_views') return 'Unique views'
      if (this.ceremonyChartMetric === 'total_shares') return 'Shares'
      if (this.ceremonyChartMetric === 'total_downloads') return 'Downloads'
      return 'Total views'
    },
    ceremonySeasonCompareJobStatusLabel() {
      const status = String(this.ceremonySeasonCompareJobStatus || '').toLowerCase()
      if (status === 'queued') return 'Queued'
      if (status === 'running') return 'Running'
      if (status === 'completed') return 'Completed'
      if (status === 'failed') return 'Failed'
      if (status === 'submitting') return 'Submitting'
      return 'Starting'
    },
    ceremonySeasonCompareLoadingText() {
      const status = String(this.ceremonySeasonCompareJobStatus || '').toLowerCase()
      if (status === 'queued') return 'Comparison job is queued and waiting for a worker.'
      if (status === 'running') return 'Computing season deltas and generating narrative insights.'
      if (status === 'submitting') return 'Submitting season comparison request...'
      return 'Preparing season comparison insights...'
    },
  },
  methods: {
    emitSeasonChange() {
      this.$emit('season-change')
    },
    emitInsightsModeChange() {
      this.$emit('insights-mode-change')
    },
    emitCompareSeasonChange() {
      this.$emit('compare-season-change')
    },
    seasonCompareMetricLabel(row) {
      return String(row?.label || row?.metric_key || 'Metric')
    },
    seasonCompareMetricChartValue(row) {
      const pct = Number(row?.delta_pct)
      if (Number.isFinite(pct)) return Math.abs(pct)
      const delta = Number(row?.delta)
      return Number.isFinite(delta) ? Math.abs(delta) : 0
    },
    seasonCompareMetricDeltaText(row) {
      const delta = Number(row?.delta || 0)
      const pct = Number(row?.delta_pct)
      const sign = delta > 0 ? '+' : ''
      if (Number.isFinite(pct)) return `${sign}${delta.toFixed(2)} (${sign}${pct.toFixed(2)}%)`
      return `${sign}${delta.toFixed(2)}`
    },
    formatScopedMetricValue(value, kind = 'count') {
      const n = Number(value)
      if (!Number.isFinite(n)) return kind === 'count' ? '0' : 'N/A'
      if (kind === 'amplification_index') return `${n.toFixed(2)}x`
      if (kind === 'rate') return n.toFixed(2)
      return Math.round(n).toLocaleString()
    },
    ceremonyBenchmarkLabel(metricKey) {
      const benchmark = this.ceremonyScopedStats?.benchmark_yearly
      const metrics = benchmark?.metrics
      const row = metrics?.[metricKey]
      return String(row?.label || '').trim()
    },
    shareReliabilityClass(level) {
      const key = String(level || '').toLowerCase()
      if (key === 'high') return 'bg-green-100 text-green-800'
      if (key === 'medium') return 'bg-amber-100 text-amber-800'
      return 'bg-gray-100 text-gray-700'
    },
    formatComparisonMetricValue(value, kind = 'count') {
      const n = Number(value || 0)
      if (kind === 'amplification_index') return `${n.toFixed(2)}x`
      if (kind === 'rate') return n.toFixed(2)
      return Math.round(n).toLocaleString()
    },
    comparisonDeltaArrow(deltaPct) {
      const n = Number(deltaPct || 0)
      if (n > 0) return '▲'
      if (n < 0) return '▼'
      return '→'
    },
    comparisonDeltaClass(deltaPct) {
      const n = Number(deltaPct || 0)
      if (n > 0) return 'text-green-700'
      if (n < 0) return 'text-red-700'
      return 'text-gray-500'
    },
    comparisonDeltaText(deltaPct) {
      const n = Number(deltaPct || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${n.toFixed(2)}%`
    },
    comparisonDeltaPointsText(delta) {
      const n = Number(delta || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${n.toFixed(2)}pp`
    },
    comparisonDeltaCountText(delta) {
      const n = Number(delta || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${Math.round(n).toLocaleString()}`
    },
    comparisonRelativeWidth(value, maxValue) {
      const n = Number(value || 0)
      const max = Number(maxValue || 0)
      if (!(max > 0)) return '0%'
      const pct = Math.max(0, Math.min(100, (100 * n) / max))
      return `${pct.toFixed(2)}%`
    },
    geoWidth(count) {
      const max = Math.max(...(this.ceremonyScopedStats.top_timezones || []).map(r => r.count), 1)
      return `${Math.round((count / max) * 100)}%`
    },
    metricValue(row, metric) {
      return Number(row?.[metric] || 0)
    },
    ceremonyMetricWidth(row, metric) {
      const max = Math.max(...this.ceremonyRowsSorted.map(r => Number(r?.[metric] || 0)), 1)
      const value = Number(row?.[metric] || 0)
      return `${Math.max(0, Math.min(100, Math.round((value / max) * 100)))}%`
    },
    engagementThemeText(theme) {
      const value = String(theme || '').trim().toLowerCase()
      if (value === 'reach') return 'Reach'
      if (value === 'action') return 'Action'
      if (value === 'source') return 'Source'
      if (value === 'geo') return 'Geo'
      if (value === 'concentration') return 'Concentration'
      return value || 'Insight'
    },
    ceremonyTitle(row) {
      const dt = row?.ceremony_datetime ? new Date(row.ceremony_datetime) : null
      if (dt && !Number.isNaN(dt.getTime())) {
        return dt.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      }
      if (row?.ceremony_datetime_text) return String(row.ceremony_datetime_text)
      return String(row?.ceremony_label || row?.ceremony_id || 'Unknown ceremony')
    },
    isUnmappedCeremonyRow(row) {
      const id = String(row?.ceremony_id || '').trim()
      const label = String(row?.ceremony_label || '').trim().toLowerCase()
      return id === 'unmapped' || label === 'unmapped'
    },
    isCeremonyExpanded(ceremonyId) {
      return Boolean(this.expandedCeremonies?.[ceremonyId])
    },
    isCeremonyTopClipsLoading(ceremonyId) {
      return Boolean(this.ceremonyTopClipsLoading?.[ceremonyId])
    },
    clipName(clip) {
      const submissionId = String(clip?.submission_id || '')
      if (submissionId && this.clipNameBySubmission?.[submissionId]) return this.clipNameBySubmission[submissionId]
      return clip?.clip_name || clip?.submission_id || clip?.registration_id || 'Clip'
    },
  },
}
</script>

<style scoped>
.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(17, 24, 39, 0.2);
  border-top-color: rgba(17, 24, 39, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
